import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../static/ContactPage.css";
import { createMessage } from "../api/ContactMessages";
import Footer from "../components/common/Footer";
import SITE from "../config/site";

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = "Name is required";
    } else if (formData.customer_name.trim().length < 2) {
      newErrors.customer_name = "Name must be at least 2 characters long";
    } else if (formData.customer_name.trim().length > 50) {
      newErrors.customer_name = "Name must be less than 50 characters";
    }

    if (!formData.customer_email) {
      newErrors.customer_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = "Please enter a valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    } else if (formData.message.trim().length > 1000) {
      newErrors.message = "Message must be less than 1000 characters";
    }

    if (formData.subject && formData.subject.length > 200) {
      newErrors.subject = "Subject must be less than 200 characters";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      await createMessage(formData);

      setSubmitStatus("success");
      setFormData({
        customer_name: "",
        customer_email: "",
        subject: "",
        message: "",
      });

      setTimeout(() => {
        setSubmitStatus("");
      }, 5000);
    } catch (error) {
      

      if (error.response && error.response.data) {
        setErrors(error.response.data);
      }
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="c-p-wrapper">
      <div className="c-p-page">
        <div className="c-p-container">
          <div className="c-p-header">
            <button className="c-p-back-button" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i>
              Back
            </button>
            <div className="c-p-header-content">
              <div className="c-p-header-icon">
                <i className="fas fa-comments"></i>
              </div>
              <h1 className="c-p-title">Contact Us</h1>
              <p className="c-p-subtitle">
                We're here to help! Our team will get back to you within 24
                hours.
              </p>
            </div>
          </div>

          <div className="c-p-content">
            <div className="c-p-grid">
              {/* Left Column - Contact Info */}
              <div className="c-p-left-column">
                <div className="c-p-info-card">
                  <h3 className="c-p-info-card-title">Get in Touch</h3>
                  <p className="c-p-info-card-subtitle">
                    Have questions about our courses or need assistance? Reach
                    out to us through any of these channels.
                  </p>

                  <div className="c-p-contact-methods">
                    <div className="c-p-contact-method">
                      <div className="c-p-contact-icon email-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="c-p-contact-details">
                        <span className="c-p-contact-label">Email</span>
                        <a
                          href={`mailto:${SITE.contact.email}`}
                          className="c-p-contact-value"
                        >
                          {SITE.contact.email}
                        </a>
                      </div>
                    </div>

                    <div className="c-p-contact-method">
                      <div className="c-p-contact-icon clock-icon">
                        <i className="fas fa-clock"></i>
                      </div>
                      <div className="c-p-contact-details">
                        <span className="c-p-contact-label">
                          Response Time
                        </span>
                        <span className="c-p-contact-value">
                          Within 24 hours
                        </span>
                      </div>
                    </div>

                    <div className="c-p-contact-method">
                      <div className="c-p-contact-icon tips-icon">
                        <i className="fas fa-lightbulb"></i>
                      </div>
                      <div className="c-p-contact-details">
                        <span className="c-p-contact-label">Quick Tips</span>
                        <span className="c-p-contact-value">
                          Include your course/account details for faster
                          support
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="c-p-social-section">
                    <span className="c-p-social-label">Follow Us</span>
                    <div className="c-p-social-links">
                      <a
                        href={SITE.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="c-p-social-link linkedin"
                        aria-label="LinkedIn"
                      >
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                      <a
                        href={SITE.social.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="c-p-social-link youtube"
                        aria-label="YouTube"
                      >
                        <i className="fab fa-youtube"></i>
                      </a>
                      <a
                        href={SITE.social.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="c-p-social-link tiktok"
                        aria-label="TikTok"
                      >
                        <i className="fab fa-tiktok"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="c-p-right-column">
                <div className="c-p-form-card">
                  <h3 className="c-p-form-title">Send a Message</h3>

                  <form className="c-p-form" onSubmit={handleSubmit}>
                    <div className="c-p-form-group">
                      <label
                        htmlFor="customer_name"
                        className="c-p-form-label"
                      >
                        Full Name <span className="c-p-required">*</span>
                      </label>
                      <input
                        type="text"
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        className={`c-p-input ${
                          errors.customer_name ? "c-p-input-error" : ""
                        }`}
                        placeholder="Enter your full name"
                        disabled={isSubmitting}
                      />
                      {errors.customer_name && (
                        <div className="c-p-error-message">
                          {errors.customer_name}
                        </div>
                      )}
                    </div>

                    <div className="c-p-form-group">
                      <label
                        htmlFor="customer_email"
                        className="c-p-form-label"
                      >
                        Email Address{" "}
                        <span className="c-p-required">*</span>
                      </label>
                      <input
                        type="email"
                        id="customer_email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        className={`c-p-input ${
                          errors.customer_email ? "c-p-input-error" : ""
                        }`}
                        placeholder="Enter your email address"
                        disabled={isSubmitting}
                      />
                      {errors.customer_email && (
                        <div className="c-p-error-message">
                          {errors.customer_email}
                        </div>
                      )}
                    </div>

                    <div className="c-p-form-group">
                      <label htmlFor="subject" className="c-p-form-label">
                        Subject{" "}
                        <span className="c-p-optional">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={`c-p-input ${
                          errors.subject ? "c-p-input-error" : ""
                        }`}
                        placeholder="What is this regarding?"
                        disabled={isSubmitting}
                      />
                      {errors.subject && (
                        <div className="c-p-error-message">
                          {errors.subject}
                        </div>
                      )}
                    </div>

                    <div className="c-p-form-group">
                      <label htmlFor="message" className="c-p-form-label">
                        Message <span className="c-p-required">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className={`c-p-textarea ${
                          errors.message ? "c-p-textarea-error" : ""
                        }`}
                        placeholder="Tell us how we can help you..."
                        rows="5"
                        disabled={isSubmitting}
                      />
                      <div className="c-p-char-count">
                        {formData.message.length}/1000 characters
                      </div>
                      {errors.message && (
                        <div className="c-p-error-message">
                          {errors.message}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="c-p-submit-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-spinner c-p-spinner"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Send Message
                        </>
                      )}
                    </button>

                    {submitStatus === "success" && (
                      <div className="c-p-success-message">
                        <i className="fas fa-check-circle"></i>
                        <div>
                          <h4>Message Sent Successfully!</h4>
                          <p>We'll get back to you within 24 hours.</p>
                        </div>
                      </div>
                    )}

                    {submitStatus === "error" && (
                      <div className="c-p-error-alert">
                        <i className="fas fa-exclamation-circle"></i>
                        <div>
                          <h4>Something went wrong</h4>
                          <p>
                            Please try again or contact us directly at{" "}
                            {SITE.contact.email}
                          </p>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;